'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  PDFCheckBox,
  PDFDocument,
  PDFDropdown,
  PDFOptionList,
  PDFRadioGroup,
  PDFTextField,
  StandardFonts,
} from 'pdf-lib';
import {
  createCleanorExportFileName,
  createPdfPreviewUrl,
  formatFileSize,
  isPdfFile,
  openPdfDocument,
  triggerBrowserDownload,
} from '@/lib/browser-pdf-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';

type FillPdfSource = {
  id: string;
  file: File;
  name: string;
  size: number;
  pageCount: number;
  previewUrl: string | null;
};

type FillableField =
  | {
      id: string;
      name: string;
      label: string;
      type: 'text';
      value: string;
    }
  | {
      id: string;
      name: string;
      label: string;
      type: 'checkbox';
      value: boolean;
    }
  | {
      id: string;
      name: string;
      label: string;
      type: 'dropdown' | 'option-list' | 'radio';
      value: string;
      options: string[];
    };

async function readSource(file: File): Promise<FillPdfSource> {
  const document = await openPdfDocument(new Uint8Array(await file.arrayBuffer()));

  try {
    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      pageCount: document.numPages,
      previewUrl: await createPdfPreviewUrl(document),
    };
  } catch {
    throw new Error(`"${file.name}" could not be opened. Use a standard unlocked PDF file.`);
  } finally {
    document.cleanup?.();
    await document.destroy?.();
  }
}

async function extractFields(file: File): Promise<FillableField[]> {
  const document = await PDFDocument.load(await file.arrayBuffer());
  const form = document.getForm();

  return form
    .getFields()
    .map((field) => {
      const name = field.getName();
      const label = name.split('.').pop() || name;

      if (field instanceof PDFTextField) {
        return {
          id: crypto.randomUUID(),
          name,
          label,
          type: 'text' as const,
          value: field.getText() ?? '',
        };
      }

      if (field instanceof PDFCheckBox) {
        return {
          id: crypto.randomUUID(),
          name,
          label,
          type: 'checkbox' as const,
          value: field.isChecked(),
        };
      }

      if (field instanceof PDFDropdown) {
        return {
          id: crypto.randomUUID(),
          name,
          label,
          type: 'dropdown' as const,
          value: field.getSelected()[0] ?? '',
          options: field.getOptions(),
        };
      }

      if (field instanceof PDFOptionList) {
        return {
          id: crypto.randomUUID(),
          name,
          label,
          type: 'option-list' as const,
          value: field.getSelected()[0] ?? '',
          options: field.getOptions(),
        };
      }

      if (field instanceof PDFRadioGroup) {
        return {
          id: crypto.randomUUID(),
          name,
          label,
          type: 'radio' as const,
          value: field.getSelected() ?? '',
          options: field.getOptions(),
        };
      }

      return null;
    })
    .filter((field): field is FillableField => field !== null);
}

function createExportFileName(pageCount: number) {
  const pageLabel = pageCount === 1 ? '1-page' : `${pageCount}-pages`;
  return createCleanorExportFileName('fill-pdf-form', [pageLabel], 'pdf');
}

export default function BrowserFillPdfForm() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const downloadUrlRef = useRef<string | null>(null);
  const [source, setSource] = useState<FillPdfSource | null>(null);
  const [fields, setFields] = useState<FillableField[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);

  const fieldSummary = useMemo(
    () => ({
      text: fields.filter((field) => field.type === 'text').length,
      toggles: fields.filter((field) => field.type === 'checkbox' || field.type === 'radio').length,
      choices: fields.filter((field) => field.type === 'dropdown' || field.type === 'option-list')
        .length,
    }),
    [fields],
  );

  useEffect(() => {
    return () => {
      if (source?.previewUrl) {
        URL.revokeObjectURL(source.previewUrl);
      }

      if (downloadUrlRef.current) {
        URL.revokeObjectURL(downloadUrlRef.current);
      }
    };
  }, [source]);

  function resetExportState() {
    if (downloadUrlRef.current) {
      URL.revokeObjectURL(downloadUrlRef.current);
    }

    downloadUrlRef.current = null;
    setResultSize(null);
  }

  function replaceSource(nextSource: FillPdfSource | null) {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return nextSource;
    });
  }

  async function handleFileSelection(file: File | null) {
    setErrorMessage(null);
    setCreditMessage(null);
    setFields([]);
    resetExportState();

    if (!file) {
      replaceSource(null);
      return;
    }

    if (!isPdfFile(file)) {
      replaceSource(null);
      setErrorMessage('Choose one fillable PDF file.');
      return;
    }

    setIsLoadingFields(true);

    try {
      const [nextSource, nextFields] = await Promise.all([readSource(file), extractFields(file)]);
      replaceSource(nextSource);
      setFields(nextFields);

      if (!nextFields.length) {
        setCreditMessage('No editable AcroForm fields were found in this PDF.');
      }
    } catch (error) {
      replaceSource(null);
      setFields([]);
      setErrorMessage(error instanceof Error ? error.message : 'This PDF could not be opened.');
    } finally {
      setIsLoadingFields(false);
    }
  }

  function clearSource() {
    replaceSource(null);
    setFields([]);
    setErrorMessage(null);
    setCreditMessage(null);
    resetExportState();

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  function updateField(id: string, nextValue: string | boolean) {
    setFields((current) =>
      current.map((field) => {
        if (field.id !== id) {
          return field;
        }

        if (field.type === 'checkbox') {
          return {
            ...field,
            value: Boolean(nextValue),
          };
        }

        return {
          ...field,
          value: String(nextValue),
        };
      }),
    );
  }

  async function saveFilledPdf() {
    if (!source) {
      setErrorMessage('Choose a PDF first.');
      return;
    }

    if (!fields.length) {
      setErrorMessage('This PDF does not expose fillable AcroForm fields.');
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'fill-pdf-form',
        'Credits are not available for another PDF form fill right now.',
      );

      const document = await PDFDocument.load(await source.file.arrayBuffer());
      const form = document.getForm();

      for (const fieldState of fields) {
        const field = form.getFieldMaybe(fieldState.name);

        if (!field) {
          continue;
        }

        if (fieldState.type === 'text' && field instanceof PDFTextField) {
          field.setText(fieldState.value);
          continue;
        }

        if (fieldState.type === 'checkbox' && field instanceof PDFCheckBox) {
          if (fieldState.value) {
            field.check();
          } else {
            field.uncheck();
          }
          continue;
        }

        if (fieldState.type === 'dropdown' && field instanceof PDFDropdown) {
          if (fieldState.value) {
            field.select(fieldState.value);
          } else {
            field.clear();
          }
          continue;
        }

        if (fieldState.type === 'option-list' && field instanceof PDFOptionList) {
          if (fieldState.value) {
            field.select(fieldState.value);
          } else {
            field.clear();
          }
          continue;
        }

        if (fieldState.type === 'radio' && field instanceof PDFRadioGroup) {
          if (fieldState.value) {
            field.select(fieldState.value);
          } else {
            field.clear();
          }
        }
      }

      const font = await document.embedFont(StandardFonts.Helvetica);
      form.updateFieldAppearances(font);

      const bytes = await document.save();
      const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' });
      const nextUrl = URL.createObjectURL(blob);
      downloadUrlRef.current = nextUrl;
      setResultSize(blob.size);
      triggerBrowserDownload(nextUrl, createExportFileName(source.pageCount));
      setCreditMessage(formatToolCreditSuccess('Form export', credits));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'This PDF form could not be saved.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Fill a PDF form in the browser</h2>
          <p>
            Open a fillable PDF, update its form fields locally, and export the completed file
            without sending it to a server.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {source ? 'PDF ready for form filling' : 'Choose one fillable PDF or drop it here'}
              </strong>
              <span>
                {source
                  ? `${source.pageCount} pages • ${formatFileSize(source.size)}`
                  : 'Works with AcroForm-based PDFs that expose editable form fields'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || !fields.length || isLoadingFields || isSaving}
              onClick={() => void saveFilledPdf()}
              type="button"
            >
              {isLoadingFields ? 'Reading fields…' : isSaving ? 'Saving PDF…' : 'Save filled PDF'}
            </button>
            {source ? (
              <button className="button button-ghost" onClick={clearSource} type="button">
                Clear file
              </button>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>AcroForm filler</h3>
          <div className="tool-mockup-chip-list">
            <span>PDF → PDF</span>
            <span>Text fields</span>
            <span>Choices</span>
            <span>Local export</span>
          </div>
          <p>
            Use this for standard fillable PDFs. If the document is only a scan, use OCR-based tools
            instead.
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>
              Only AcroForm fields can be filled here. PDFs built as flat images will not expose
              editable fields.
            </span>
          </div>
          {source ? (
            <div className="tool-live-stats">
              <div>
                <strong>Pages</strong>
                <span>{source.pageCount}</span>
              </div>
              <div>
                <strong>Fields</strong>
                <span>{fields.length}</span>
              </div>
              <div>
                <strong>Text inputs</strong>
                <span>{fieldSummary.text}</span>
              </div>
              <div>
                <strong>Toggles</strong>
                <span>{fieldSummary.toggles}</span>
              </div>
              <div>
                <strong>Choices</strong>
                <span>{fieldSummary.choices}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>Last export</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </aside>
      </div>

      {source?.previewUrl ? (
        <div className="tool-live-preview">
          <h3>Source preview</h3>
          <div className="tool-live-preview-frame">
            <img alt="" src={source.previewUrl} />
          </div>
        </div>
      ) : null}

      {source ? (
        <div className="tool-live-preview">
          <h3>Form fields</h3>
          <div className="tool-form-field-list">
            {fields.length ? (
              fields.map((field) => (
                <label className="tool-live-field tool-form-field-card" key={field.id}>
                  <span>{field.label}</span>
                  {field.type === 'text' ? (
                    <input
                      onChange={(event) => updateField(field.id, event.target.value)}
                      value={field.value}
                    />
                  ) : null}
                  {field.type === 'checkbox' ? (
                    <span className="tool-form-checkbox-row">
                      <input
                        checked={field.value}
                        onChange={(event) => updateField(field.id, event.target.checked)}
                        type="checkbox"
                      />
                      <small>{field.name}</small>
                    </span>
                  ) : null}
                  {field.type === 'dropdown' ||
                  field.type === 'option-list' ||
                  field.type === 'radio' ? (
                    <select
                      onChange={(event) => updateField(field.id, event.target.value)}
                      value={field.value}
                    >
                      <option value="">No selection</option>
                      {field.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  <small>{field.name}</small>
                </label>
              ))
            ) : (
              <div className="tool-live-panel-note">
                <strong>No fillable fields found</strong>
                <span>
                  This PDF looks flat. Try OCR or scan-cleanup tools if you need editable output.
                </span>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </article>
  );
}
